import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config/api';
import {
  TrendingUp,
  TrendingDown,
  Target,
  Clock,
  Award,
  Users,
  Brain,
  Zap,
  Calendar,
  BarChart3,
  PieChart,
  Activity,
  Flame,
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
  Loader2,
  BookOpen,
  Sparkles
} from 'lucide-react';
import { Progress } from './ui/progress';
const API = `${API_BASE}/api`;

/**
 * LearnerInsights Component
 * Displays comprehensive progress analytics for learners including:
 * - Assessment scores over time (line chart)
 * - Knowledge gaps and strengths
 * - Time spent metrics
 * - Engagement patterns
 * - Peer comparison (radar-style)
 */
export default function LearnerInsights({ token }) {
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInsights();
  }, [token]);

  const fetchInsights = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/insights/learner`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInsights(response.data);
    } catch (err) {
      console.error('Failed to fetch insights:', err);
      setError('Unable to load your progress insights');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-8">
        <div className="flex items-center justify-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-navigate-gold" />
          <span className="text-zinc-400">Loading your insights...</span>
        </div>
      </div>
    );
  }

  if (error || !insights) {
    return (
      <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-8 text-center">
        <Brain className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">No insights available yet</h3>
        <p className="text-zinc-400 text-sm">
          Complete some assessments to see your progress insights.
        </p>
      </div>
    );
  }

  const { overall_stats, score_trends, knowledge_gaps, time_metrics, engagement, peer_comparison } = insights;

  return (
    <div className="space-y-6" data-testid="learner-insights">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Activity className="h-5 w-5 text-navigate-gold" />
            Your Progress Insights
          </h2>
          <p className="text-sm text-zinc-400 mt-1">
            Track your learning journey and identify areas for improvement
          </p>
        </div>
        <div className="text-xs text-zinc-500">
          Updated {new Date(insights.generated_at).toLocaleDateString()}
        </div>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={Award}
          value={overall_stats?.average_score || 0}
          suffix="/10"
          label="Avg Score"
          color="text-navigate-gold"
          trend={overall_stats?.improvement_trend}
        />
        <StatCard
          icon={CheckCircle2}
          value={overall_stats?.pass_rate || 0}
          suffix="%"
          label="Pass Rate"
          color="text-green-400"
        />
        <StatCard
          icon={BookOpen}
          value={overall_stats?.courses_completed || 0}
          label="Completed"
          color="text-blue-400"
        />
        <StatCard
          icon={Flame}
          value={engagement?.current_streak || 0}
          suffix=" days"
          label="Streak"
          color="text-orange-400"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Score Trends Chart */}
        <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-[#1EBFB3]" />
            Assessment Scores Over Time
          </h3>
          {score_trends?.has_data ? (
            <ScoreTrendChart scores={score_trends.scores} />
          ) : (
            <EmptyState message="Complete assessments to see your score trends" />
          )}
          
          {score_trends?.has_data && (
            <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-zinc-700">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{score_trends.best_score}</div>
                <div className="text-xs text-zinc-500">Best Score</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{score_trends.total_assessments}</div>
                <div className="text-xs text-zinc-500">Total Assessments</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold ${score_trends.improvement_rate >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {score_trends.improvement_rate > 0 ? '+' : ''}{score_trends.improvement_rate}%
                </div>
                <div className="text-xs text-zinc-500">Improvement</div>
              </div>
            </div>
          )}
        </div>

        {/* Peer Comparison */}
        <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-400" />
            Peer Comparison
          </h3>
          {peer_comparison?.has_data ? (
            <>
              <PeerComparisonChart comparison={peer_comparison} />
              <p className="text-sm text-center text-zinc-400 mt-4 px-4 py-2 bg-zinc-800/50 rounded-lg">
                {peer_comparison.comparison_text}
              </p>
            </>
          ) : (
            <EmptyState message="More learner data needed for comparison" />
          )}
        </div>

        {/* Knowledge Gaps & Strengths */}
        <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Brain className="h-5 w-5 text-amber-400" />
            Knowledge Analysis
          </h3>
          {knowledge_gaps?.has_data ? (
            <div className="space-y-6">
              {/* Strengths */}
              <div>
                <h4 className="text-sm font-medium text-green-400 mb-2 flex items-center gap-1">
                  <Sparkles className="h-4 w-4" />
                  Your Strengths
                </h4>
                {knowledge_gaps.strengths?.length > 0 ? (
                  <ul className="space-y-2">
                    {knowledge_gaps.strengths.slice(0, 3).map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-zinc-300">
                        <CheckCircle2 className="h-4 w-4 text-green-400 shrink-0 mt-0.5" />
                        <span className="capitalize">{item.strength}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-zinc-500">Strengths will appear after more assessments</p>
                )}
              </div>
              
              {/* Gaps */}
              <div>
                <h4 className="text-sm font-medium text-amber-400 mb-2 flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4" />
                  Areas to Improve
                </h4>
                {knowledge_gaps.gaps?.length > 0 ? (
                  <ul className="space-y-2">
                    {knowledge_gaps.gaps.slice(0, 3).map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-zinc-300">
                        <Target className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                        <span className="capitalize">{item.gap}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-zinc-500">No significant gaps identified</p>
                )}
              </div>
            </div>
          ) : (
            <EmptyState message="Complete assessments to identify knowledge patterns" />
          )}
        </div>

        {/* Time & Engagement */}
        <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-cyan-400" />
            Time & Engagement
          </h3>
          <div className="space-y-4">
            {/* Time Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-white">
                  {time_metrics?.total_time_hours || 0}h
                </div>
                <div className="text-xs text-zinc-500">Total Learning Time</div>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-white">
                  {time_metrics?.average_session_minutes || 0}m
                </div>
                <div className="text-xs text-zinc-500">Avg Session</div>
              </div>
            </div>

            {/* Engagement Metrics */}
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-zinc-400">Consistency (30 days)</span>
                  <span className="text-white">{engagement?.consistency_score || 0}%</span>
                </div>
                <Progress value={engagement?.consistency_score || 0} className="h-2" />
              </div>
              
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-zinc-400">Course Completion</span>
                  <span className="text-white">{engagement?.completion_rate || 0}%</span>
                </div>
                <Progress value={engagement?.completion_rate || 0} className="h-2" />
              </div>
            </div>

            {/* Engagement Facts */}
            <div className="flex items-center justify-between text-sm pt-3 border-t border-zinc-700">
              <span className="text-zinc-400">Most active day</span>
              <span className="text-white font-medium">{engagement?.most_active_day || 'N/A'}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-400">Active days (total)</span>
              <span className="text-white font-medium">{engagement?.total_active_days || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Activity Chart */}
      {time_metrics?.time_by_day?.length > 0 && (
        <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-green-400" />
            Daily Learning Activity (Last 30 Days)
          </h3>
          <DailyActivityChart data={time_metrics.time_by_day} />
        </div>
      )}
    </div>
  );
}

// Stat Card Component
function StatCard({ icon: Icon, value, suffix = '', label, color, trend }) {
  return (
    <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors">
      <div className="flex items-center justify-between mb-2">
        <Icon className={`h-5 w-5 ${color}`} />
        {trend && (
          <span className={`text-xs px-1.5 py-0.5 rounded ${
            trend === 'improving' ? 'bg-green-500/20 text-green-400' :
            trend === 'needs_attention' ? 'bg-red-500/20 text-red-400' :
            'bg-zinc-500/20 text-zinc-400'
          }`}>
            {trend === 'improving' ? '↑' : trend === 'needs_attention' ? '↓' : '→'}
          </span>
        )}
      </div>
      <div className="text-2xl font-bold text-white">
        {value}{suffix}
      </div>
      <div className="text-xs text-zinc-500">{label}</div>
    </div>
  );
}

// Empty State Component
function EmptyState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <PieChart className="h-10 w-10 text-zinc-600 mb-3" />
      <p className="text-sm text-zinc-500">{message}</p>
    </div>
  );
}

// Score Trend Chart (Simple bar visualization)
function ScoreTrendChart({ scores }) {
  if (!scores || scores.length === 0) return <EmptyState message="No score data" />;
  
  const maxScore = 10;
  const displayScores = scores.slice(-10); // Last 10 scores
  
  return (
    <div className="space-y-2">
      <div className="flex items-end justify-between gap-1 h-32">
        {displayScores.map((item, idx) => {
          const height = (item.score / maxScore) * 100;
          return (
            <div
              key={idx}
              className="flex-1 flex flex-col items-center gap-1"
              title={`${item.course_title}: ${item.score}/10`}
            >
              <div
                className={`w-full rounded-t transition-all ${
                  item.passed ? 'bg-gradient-to-t from-green-600 to-green-400' : 'bg-gradient-to-t from-amber-600 to-amber-400'
                }`}
                style={{ height: `${height}%`, minHeight: '4px' }}
              />
              <span className="text-[10px] text-zinc-500">{item.score}</span>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-zinc-600">
        <span>Older</span>
        <span>Recent</span>
      </div>
    </div>
  );
}

// Peer Comparison Visual
function PeerComparisonChart({ comparison }) {
  const metrics = [
    { label: 'Avg Score', user: comparison.user_avg_score, peer: comparison.peer_avg_score, max: 10 },
    { label: 'Pass Rate', user: comparison.user_pass_rate, peer: comparison.peer_pass_rate, max: 100, suffix: '%' },
    { label: 'Completion', user: comparison.user_completion_rate, peer: comparison.peer_completion_rate, max: 100, suffix: '%' }
  ];

  return (
    <div className="space-y-4">
      {metrics.map((metric, idx) => (
        <div key={idx} className="space-y-1">
          <div className="flex justify-between text-xs text-zinc-400">
            <span>{metric.label}</span>
            <span>
              You: <span className="text-white">{metric.user}{metric.suffix || ''}</span>
              {' vs '}
              Peers: <span className="text-zinc-300">{metric.peer}{metric.suffix || ''}</span>
            </span>
          </div>
          <div className="relative h-3 bg-zinc-800 rounded-full overflow-hidden">
            {/* Peer bar (background) */}
            <div
              className="absolute inset-y-0 left-0 bg-zinc-600 rounded-full"
              style={{ width: `${(metric.peer / metric.max) * 100}%` }}
            />
            {/* User bar (foreground) */}
            <div
              className={`absolute inset-y-0 left-0 rounded-full ${
                metric.user >= metric.peer ? 'bg-navigate-gold' : 'bg-amber-500'
              }`}
              style={{ width: `${(metric.user / metric.max) * 100}%` }}
            />
          </div>
        </div>
      ))}
      
      <div className="flex items-center justify-center gap-4 pt-2 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-navigate-gold rounded" />
          <span className="text-zinc-400">You</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-zinc-600 rounded" />
          <span className="text-zinc-400">Peer Average</span>
        </div>
      </div>
      
      <div className="text-center pt-2">
        <span className="text-sm font-medium text-navigate-gold">
          Top {100 - comparison.score_percentile}% of learners
        </span>
      </div>
    </div>
  );
}

// Daily Activity Chart (Heat map style)
function DailyActivityChart({ data }) {
  if (!data || data.length === 0) return null;
  
  const maxMinutes = Math.max(...data.map(d => d.minutes), 1);
  
  return (
    <div className="space-y-2">
      <div className="flex gap-[2px]">
        {data.map((day, idx) => {
          const intensity = day.minutes / maxMinutes;
          const opacity = day.minutes > 0 ? Math.max(0.2, intensity) : 0.05;
          return (
            <div
              key={idx}
              className="flex-1 h-8 rounded-sm transition-colors cursor-pointer hover:ring-1 hover:ring-navigate-gold"
              style={{
                backgroundColor: day.minutes > 0 
                  ? `rgba(212, 175, 55, ${opacity})` 
                  : 'rgba(63, 63, 70, 0.3)'
              }}
              title={`${day.date}: ${day.minutes} min`}
            />
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-zinc-600">
        <span>{data[0]?.date?.slice(5)}</span>
        <span>30 days</span>
        <span>{data[data.length - 1]?.date?.slice(5)}</span>
      </div>
    </div>
  );
}
